/**
 * Which basic+ questions the PA page shows (LAM-5853): the rule sets alone decide, answered or not, and
 * only a shown question's answer opens its follow-ups, so a hidden parent hides its children too.
 *
 * Mirrors backend `requirement_visibility.basic_plus_shown_rules`. The basic+ section, the
 * send-to-prescriber format gate and the submit serializer all use this one rule, so what is shown,
 * what is checked and what is sent agree.
 *
 * Example: `benefit_type = pharmacy` hides `date_of_administration` (shown for `medical` only), even
 * when autocomplete stored an answer for it.
 */
function hasLabel(answer) {
    return (answer.options ?? []).some((option) => String(option?.label ?? "").trim());
}
function shows(question, answered, selected) {
    if (question.invisibleInPortal)
        return false;
    const parents = question.requirementRuleSet ?? [];
    if (!parents.length)
        return true;
    if (!parents.every((parent) => answered.has(parent)))
        return false;
    return (question.requirementOptions ?? []).some((option) => (option.optionRuleSet ?? []).every((rule) => selected.has(rule)) &&
        (option.electiveOptionRuleSet ?? [])
            .filter((group) => Boolean(group?.length))
            .every((group) => group.some((rule) => selected.has(rule))));
}
/** Rule names of the basic+ questions the page shows, given the answers (checklist shape). */
export function basicPlusShownRules(questions, answers) {
    const given = answers.filter((answer) => answer && hasLabel(answer));
    let shown = new Set(questions.map((question) => question.requirementRuleName));
    // Each pass can only hide more (fewer answers count), so this settles within one pass per question.
    for (let pass = 0; pass <= questions.length; pass++) {
        const counted = given.filter((answer) => shown.has(answer.id));
        const answered = new Set(counted.map((answer) => answer.id));
        const selected = new Set(counted.flatMap((answer) => (answer.options ?? []).map((option) => String(option?.id))));
        const next = new Set(questions.filter((question) => shows(question, answered, selected)).map((q) => q.requirementRuleName));
        if (next.size === shown.size && [...next].every((rule) => shown.has(rule)))
            break;
        shown = next;
    }
    return shown;
}
